import axios from 'axios';
import { API_CONFIG } from '../config/env';

export interface EmbedData {
  url: string;
  title?: string;
  height?: string;
  width?: string;
  allowFullscreen?: boolean;
  sandbox?: string;
}

export interface SessionData {
  id: string | number;
  transactionId: number;
  paymentMethod: string;
  url: string;
  status: string;
  createdAt: string;
  expireAt: string;
  fiatCurrency: string;
  fiatAmount: number;
  cryptoCurrency: string;
  cryptoAmount: number | null;
  uniqueWalletAddress: string;
}

class EmbedService {
  private unwrapPayload<T>(payload: unknown): T | null {
    if (payload === null || payload === undefined) return null;
    if (typeof payload !== 'object') return payload as T;

    const obj = payload as Record<string, unknown>;
    const candidates = ['data', 'result', 'value'];
    for (const key of candidates) {
      if (obj[key] !== undefined && obj[key] !== null) {
        return obj[key] as T;
      }
    }
    return payload as T;
  }

  private normalizeSessionData(payload: unknown): SessionData {
    const raw = this.unwrapPayload<Record<string, unknown>>(payload) ?? {};
    return {
      id:
        typeof raw.id === 'string' || typeof raw.id === 'number'
          ? raw.id
          : '',
      transactionId: Number(raw.transactionId ?? 0),
      paymentMethod: typeof raw.paymentMethod === 'string' ? raw.paymentMethod : '',
      url: typeof raw.url === 'string' ? raw.url.trim() : '',
      status: typeof raw.status === 'string' ? raw.status : '',
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
      expireAt: typeof raw.expireAt === 'string' ? raw.expireAt : '',
      fiatCurrency:
        typeof raw.fiatCurrency === 'string'
          ? raw.fiatCurrency
          : typeof raw.FiatCurrency === 'string'
            ? raw.FiatCurrency
            : '',
      fiatAmount: Number(raw.fiatAmount ?? raw.FiatAmount ?? 0),
      cryptoCurrency:
        typeof raw.cryptoCurrency === 'string'
          ? raw.cryptoCurrency
          : typeof raw.CryptoCurrency === 'string'
            ? raw.CryptoCurrency
            : '',
      cryptoAmount:
        raw.cryptoAmount != null
          ? Number(raw.cryptoAmount)
          : raw.CryptoAmount != null
            ? Number(raw.CryptoAmount)
            : null,
      uniqueWalletAddress:
        typeof raw.uniqueWalletAddress === 'string'
          ? raw.uniqueWalletAddress
          : typeof raw.UniqueWalletAddress === 'string'
            ? raw.UniqueWalletAddress
            : '',
    };
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get embed data for a unique hash
   * @param uniqueHash - The unique hash string from the URL
   * @returns Promise<EmbedData>
   */
  async getEmbedData(uniqueHash: string): Promise<EmbedData> {
    try {
      const response = await axios.get<EmbedData>(`${API_CONFIG.BASE_URL}/embed/${uniqueHash}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching embed data for ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Create a new embed link
   * @param embedData - The embed configuration
   * @returns Promise<{ uniqueHash: string }>
   */
  async createEmbedLink(embedData: Omit<EmbedData, 'uniqueHash'>): Promise<{ uniqueHash: string }> {
    try {
      const response = await axios.post<{ uniqueHash: string }>(`${API_CONFIG.BASE_URL}/embed`, embedData, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating embed link:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Update an existing embed link
   * @param uniqueHash - The unique hash string
   * @param embedData - The updated embed configuration
   * @returns Promise<EmbedData>
   */
  async updateEmbedLink(uniqueHash: string, embedData: Partial<EmbedData>): Promise<EmbedData> {
    try {
      const response = await axios.put<EmbedData>(`${API_CONFIG.BASE_URL}/embed/${uniqueHash}`, embedData, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating embed link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Delete an embed link
   * @param uniqueHash - The unique hash string
   * @returns Promise<void>
   */
  async deleteEmbedLink(uniqueHash: string): Promise<void> {
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/embed/${uniqueHash}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error(`Error deleting embed link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get all embed links for the current user
   * @returns Promise<Array<{ uniqueHash: string; title?: string; createdAt: string; updatedAt: string }>>
   */
  async getEmbedLinks(): Promise<Array<{ uniqueHash: string; title?: string; createdAt: string; updatedAt: string }>> {
    try {
      const response = await axios.get<Array<{ uniqueHash: string; title?: string; createdAt: string; updatedAt: string }>>(`${API_CONFIG.BASE_URL}/embed`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching embed links:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get session data for a session ID (unauthenticated endpoint)
   * @param sessionId - The session ID (alphanumeric string)
   * @returns Promise<SessionData>
   */
  async getSessionData(sessionId: string): Promise<SessionData> {
    try {
      // Validate that sessionId is a non-empty string (now accepts alphanumeric format)
      if (!sessionId || sessionId.trim().length === 0) {
        throw new Error('Invalid session ID format');
      }

      // Trim and use the sessionId directly - axios will handle URL encoding
      const sanitizedSessionId = sessionId.trim();

      const response = await axios.get(`${API_CONFIG.BASE_URL}/Transactions/sessions/${sanitizedSessionId}`, {
        // No authentication headers for this endpoint
      });
      return this.normalizeSessionData(response.data);
    } catch (error) {
      console.error(`Error fetching session data for ${sessionId}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }
}

export const embedService = new EmbedService();
